import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FocusEvent as ReactFocusEvent, ReactElement, ReactNode } from 'react';
import { cn } from './cn';
import { IconButton } from './IconButton';
import { AlertCircle, AlertTriangle, Check, Info, X } from './icons';
import { Portal } from './Portal';
import { ToastContext } from './ToastContext';
import type { ToastApi, ToastOptions, ToastRecord, ToastTone } from './ToastContext';

export const DEFAULT_TOAST_DURATION = 5000;

interface TimerState {
  handle: number;
  startedAt: number;
  remaining: number;
}

const TONE_ICON: Record<ToastTone, ReactElement> = {
  info: <Info />,
  success: <Check />,
  warning: <AlertTriangle />,
  error: <AlertCircle />,
};

const TONE_ACCENT: Record<ToastTone, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-danger',
};

let sequence = 0;

interface ToastItemProps {
  toast: ToastRecord;
  dismissLabel: string;
  onDismiss: (id: string) => void;
  onPause: () => void;
  onResume: () => void;
}

function ToastItem({
  toast,
  dismissLabel,
  onDismiss,
  onPause,
  onResume,
}: ToastItemProps): ReactElement {
  const isError = toast.tone === 'error';

  function handleBlur(event: ReactFocusEvent<HTMLDivElement>): void {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    onResume();
  }

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      onMouseEnter={onPause}
      onMouseLeave={onResume}
      onFocus={onPause}
      onBlur={handleBlur}
      className={cn(
        'pointer-events-auto flex w-full max-w-96 items-start gap-2.5 rounded-lg border',
        'border-border bg-surface-raised p-3 text-ink shadow-pop',
      )}
    >
      <span
        aria-hidden="true"
        className={cn('mt-px flex shrink-0 [&>svg]:h-4 [&>svg]:w-4', TONE_ACCENT[toast.tone])}
      >
        {TONE_ICON[toast.tone]}
      </span>
      <div className="min-w-0 flex-1">
        {toast.title && <p className="text-[13px] font-semibold">{toast.title}</p>}
        <p className="text-[13px] leading-5 break-words text-ink-muted">{toast.message}</p>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
            className="mt-1.5 text-[13px] font-medium text-accent underline-offset-2 hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <IconButton
        label={dismissLabel}
        icon={<X />}
        compact
        tooltip={false}
        className="-mt-1 -mr-1"
        onClick={() => onDismiss(toast.id)}
      />
    </div>
  );
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Accessible name of the live region. */
  label?: string;
  dismissLabel?: string;
  /** Oldest toasts beyond this count are dropped. */
  max?: number;
}

/**
 * Toast host.
 *
 * Errors never time out — they are the one class of message a user must be able
 * to read at their own pace — and any hover or keyboard focus inside the stack
 * suspends every running timer until the pointer leaves.
 */
export function ToastProvider({
  children,
  label = 'Notifications',
  dismissLabel = 'Dismiss',
  max = 4,
}: ToastProviderProps): ReactElement {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const timers = useRef(new Map<string, TimerState>());
  const paused = useRef(false);

  const clearTimer = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (!timer) return;
    window.clearTimeout(timer.handle);
    timers.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      clearTimer(id);
      setToasts((previous) => previous.filter((toast) => toast.id !== id));
    },
    [clearTimer],
  );

  const startTimer = useCallback(
    (id: string, duration: number) => {
      if (!Number.isFinite(duration) || duration <= 0) return;
      timers.current.set(id, {
        handle: window.setTimeout(() => dismiss(id), duration),
        startedAt: Date.now(),
        remaining: duration,
      });
    },
    [dismiss],
  );

  const pause = useCallback(() => {
    if (paused.current) return;
    paused.current = true;
    const now = Date.now();
    for (const timer of timers.current.values()) {
      window.clearTimeout(timer.handle);
      timer.remaining = Math.max(0, timer.remaining - (now - timer.startedAt));
    }
  }, []);

  const resume = useCallback(() => {
    if (!paused.current) return;
    paused.current = false;
    const now = Date.now();
    for (const [id, timer] of timers.current) {
      if (timer.remaining <= 0) continue;
      timer.startedAt = now;
      timer.handle = window.setTimeout(() => dismiss(id), timer.remaining);
    }
  }, [dismiss]);

  const api = useMemo<ToastApi>(() => {
    function show(message: string, options: ToastOptions = {}): string {
      const tone = options.tone ?? 'info';
      const duration = options.duration ?? (tone === 'error' ? 0 : DEFAULT_TOAST_DURATION);
      sequence += 1;
      const id = `toast-${sequence}`;
      const record: ToastRecord = {
        id,
        message,
        tone,
        title: options.title,
        duration,
        action: options.action,
      };
      setToasts((previous) => [...previous, record].slice(-max));
      if (!paused.current) startTimer(id, duration);
      return id;
    }

    function dismissAll(): void {
      for (const id of [...timers.current.keys()]) clearTimer(id);
      setToasts([]);
    }

    return { show, dismiss, dismissAll };
  }, [clearTimer, dismiss, max, startTimer]);

  // Reap timers whose toast has gone, including ones dropped by the `max` cap.
  useEffect(() => {
    const live = new Set(toasts.map((toast) => toast.id));
    for (const [id, timer] of timers.current) {
      if (live.has(id)) continue;
      window.clearTimeout(timer.handle);
      timers.current.delete(id);
    }
  }, [toasts]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) window.clearTimeout(timer.handle);
      pending.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Portal>
        <div
          role="region"
          aria-label={label}
          aria-live="polite"
          className={cn(
            'fs-safe-bottom pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex flex-col',
            'items-center gap-2 p-3 sm:inset-x-auto sm:right-0 sm:items-end sm:p-4',
          )}
        >
          {toasts.map((toast) => (
            <ToastItem
              key={toast.id}
              toast={toast}
              dismissLabel={dismissLabel}
              onDismiss={dismiss}
              onPause={pause}
              onResume={resume}
            />
          ))}
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}
